import { PatternFormat } from 'react-number-format';

import TextArea from '~/common/componentMapper/Input/TextArea';
import DatePicker from '~/common/components/DatePicker';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import InputComponent from '~/common/components/Inputs/Input';
import PercentageInput from '~/common/components/Inputs/PercentageInput';
import { US_STATES_LABEL_VALUE_ARRAY } from '~/common/constants';
import { reverseFormatCurrency } from '~/common/util/format';

import Radio from '../components/Inputs/Radio';
import Select from '../components/Inputs/Select';
import { Switch } from '../components/Inputs/Switch';

type InputMapperProps = {
  hasError?: boolean;
  inputType?: string;
  field?: any;
  question?: any;
  disabled?: boolean;
  maxInputWidth?: string;
  width?: string;
  tabIndex?: number;
  inputRef?: any;
};

/**
 * @typedef {object} InputMapperProps
 * @property {string} inputType
 * @property {boolean} hasError
 * @property {object} field
 * @property {import('../types/question').QuestionInput} question
 * @property {boolean} [disabled=false]
 *
 * @param {InputMapperProps} props
 * @returns {JSX.Element}
 */
export default function InputMapper({
  inputType,
  hasError,
  field,
  question,
  disabled = false,
  maxInputWidth = '240px',
  ...props
}: InputMapperProps) {
  const { sx } = question;

  const getNumberInput = () => {
    if (!!question?.format && typeof question.format === 'string') {
      return (
        <PatternFormat
          format={question.format}
          mask="_"
          {...field}
          tabIndex={props.tabIndex}
          onChange={field.onChange}
          defaultValue={field.value}
          customInput={InputComponent}
          data-testid={question.name}
          getInputRef={props.inputRef}
          isInvalid={hasError}
          isDisabled={disabled}
          isReadOnly={question.isReadOnly}
        />
      );
    }
    return (
      <InputComponent
        type="number"
        sx={sx ?? { maxWidth: maxInputWidth }}
        isInvalid={hasError}
        isDisabled={disabled}
        isReadOnly={disabled}
        data-testid={question.name}
        defaultValue={field.value}
        {...field}
      />
    );
  };
  const simpleInputMap: Record<string, JSX.Element> = {
    currency: (
      <CurrencyInput
        isDisabled={disabled}
        isInvalid={hasError}
        {...field}
        {...props}
        aria-label={question.ariaLabel}
        data-testid={question.name}
        defaultValue={field.value}
        isReadOnly={question.isReadOnly}
        sx={sx ?? { maxWidth: maxInputWidth }}
        handleCurrencyChange={(_, e) => {
          field.onChange(reverseFormatCurrency(e));
        }}
      />
    ),
    date: (
      <DatePicker
        error={hasError}
        isDisabled={disabled}
        sx={sx ?? { maxWidth: maxInputWidth }}
        data-testid={question.name}
        defaultValue={field.value}
        {...question}
        {...field}
        label={undefined}
      />
    ),
    text: (
      <InputComponent
        sx={sx ?? { maxWidth: maxInputWidth }}
        isInvalid={hasError}
        isDisabled={disabled}
        data-testid={question.name}
        defaultValue={field.value}
        isReadOnly={question.isReadOnly}
        {...field}
      />
    ),
    number: getNumberInput(),
    percentage: (
      <PercentageInput
        type="percentage"
        sx={sx ?? { maxWidth: maxInputWidth }}
        isInvalid={hasError}
        isDisabled={disabled}
        isReadOnly={disabled}
        data-testid={question.name}
        defaultValue={field.value}
        {...field}
      />
    ),
    select: (
      <Select
        isInvalid={hasError}
        field={field}
        question={question}
        options={question.options}
        maxInputWidth={maxInputWidth}
        disabled={disabled}
      />
    ),
    stateSelect: (
      <Select
        isInvalid={hasError}
        field={field}
        question={question}
        options={US_STATES_LABEL_VALUE_ARRAY}
        maxInputWidth={maxInputWidth}
      />
    ),
    textarea: (
      <TextArea data-testid={question.name} isInvalid={hasError} {...field} />
    ),
    radio: (
      <Radio
        field={field}
        question={question}
        isInvalid={hasError}
        data-testid={question.name}
      />
    ),
    switch: (
      <Switch
        {...field}
        disabled={disabled}
        data-testid={question.name}
        ariaLabel={question.ariaLabel}
      />
    ),
    hidden: (
      <InputComponent display="none" data-testid={question.name} {...field} />
    ),
  };

  return (
    simpleInputMap[inputType] ?? (
      <p>
        No input with type of <strong>{inputType}</strong>
      </p>
    )
  );
}
